import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { ExternalLink } from 'lucide-react';
import { theme } from '@styles/theme';
import { Card, CardContent, StatusBadge, Spinner, getPrototypeStatusType } from '@components/index';
import { usePrototypes } from '@hooks/index';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;
const PrototypeGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.md};
`;
const PrototypeCard = styled(Card) `
  transition: all ${theme.transitions.fast};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};

  ${({ $isClickable }) => $isClickable &&
    `
    &:hover {
      border-color: ${theme.colors.border.secondary};
      transform: translateY(-2px);
    }
  `}
`;
const PrototypeHeader = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;
const PrototypeName = styled.h3 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;
const PrototypeDescription = styled.p `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;
const LaunchHint = styled.div `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
`;
const UnavailableMessage = styled.div `
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
`;
const LoadingContainer = styled.div `
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.xl};
`;
const EmptyState = styled.div `
  text-align: center;
  padding: ${theme.spacing.xxl};
`;
const EmptyStateTitle = styled.h3 `
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.sm};
`;
const EmptyStateDescription = styled.p `
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.muted};
  max-width: 400px;
  margin: 0 auto;
`;
const ErrorMessage = styled.div `
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  text-align: center;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.status.error}20;
  border-radius: ${theme.borderRadius.md};
`;
export function ClientDashboard() {
    const { prototypes, isLoading, error } = usePrototypes();
    const handlePrototypeClick = (deploymentUrl) => {
        if (deploymentUrl) {
            window.open(deploymentUrl, '_blank', 'noopener,noreferrer');
        }
    };
    if (isLoading) {
        return (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (error) {
        return _jsx(ErrorMessage, { children: error });
    }
    if (prototypes.length === 0) {
        return (_jsxs(EmptyState, { children: [_jsx(EmptyStateTitle, { children: "No prototypes available" }), _jsx(EmptyStateDescription, { children: "You don't have access to any prototypes yet. Please contact your administrator for access." })] }));
    }
    return (_jsx(Container, { children: _jsx(PrototypeGrid, { children: prototypes.map((prototype) => {
                const isDeployed = prototype.status === 'deployed' && prototype.deploymentUrl;
                return (_jsx(PrototypeCard, { "$isClickable": !!isDeployed, onClick: () => handlePrototypeClick(prototype.deploymentUrl), children: _jsxs(CardContent, { children: [_jsxs(PrototypeHeader, { children: [_jsx(PrototypeName, { children: prototype.name }), _jsx(StatusBadge, { status: getPrototypeStatusType(prototype.status), size: "sm", dot: true, children: prototype.status })] }), prototype.description && _jsx(PrototypeDescription, { children: prototype.description }), isDeployed ? (_jsxs(LaunchHint, { children: [_jsx(ExternalLink, { size: 12 }), "Click to launch prototype"] })) : (_jsx(UnavailableMessage, { children: prototype.status === 'building' || prototype.status === 'deploying'
                                    ? 'Deployment in progress...'
                                    : prototype.status === 'failed'
                                        ? 'Deployment failed. Contact administrator.'
                                        : 'Not yet deployed' }))] }) }, prototype.id));
            }) }) }));
}
